/**
 * VAPID public-key fetcher (client-side).
 *
 * Backend ``GET /api/notifications/vapid-public-key`` is exempt from
 * auth and returns ``Cache-Control: public, max-age=3600`` — meaning
 * the browser's HTTP cache already covers the "called twice in
 * quick succession" case at near-zero cost. We previously kept a
 * module-level memoized Promise on top of that, but the memo had
 * session lifetime: if the backend rotated keys mid-session (or
 * recovered from a 503 with a freshly-deployed key), the stale
 * decoded key stayed cached until full reload — and subscribe
 * attempts kept failing (Bugbot PR-1642 BB11). Drop the memo and
 * trust the HTTP cache; subscribe is called at most a handful of
 * times per session.
 *
 * Decoded into the Uint8Array shape pushManager.subscribe wants for
 * ``applicationServerKey``.
 */

import { getApiBaseUrl } from '@/lib/api-config'

export async function getVapidPublicKey(): Promise<Uint8Array> {
  // ``no-store`` would defeat the backend's max-age=3600. ``default``
  // (no override) lets the browser HTTP cache honor that header.
  const resp = await fetch(
    `${getApiBaseUrl()}/api/notifications/vapid-public-key`,
  )
  if (!resp.ok) {
    throw new Error(`vapid-public-key fetch failed (${resp.status})`)
  }
  const body = (await resp.json()) as { data?: { publicKey?: string } }
  const raw = body?.data?.publicKey
  if (!raw) {
    throw new Error('vapid-public-key response missing data.publicKey')
  }
  return urlBase64ToUint8Array(raw)
}

/** base64url → Uint8Array (browser ``pushManager.subscribe`` requires this). */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(base64)
  const output = new Uint8Array(raw.length)
  for (let i = 0; i < raw.length; i += 1) output[i] = raw.charCodeAt(i)
  return output
}
